'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import Badge from '@/components/shared/Badge';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import {
  mockListingsRE,
  marketSnapshotData,
  specialistSpotlight,
  ownerResources,
  RealEstateListing,
} from '@/data/mockRealEstate';
import { Home, TrendingUp, User, ArrowRight, Info, Building2 } from 'lucide-react';

const listingFilters = [
  { label: 'All', value: 'all' },
  { label: 'For Sale', value: 'listing' },
  { label: 'Rentals', value: 'rental' },
  { label: 'Tower 1', value: 'Tower 1' },
  { label: 'Tower 2', value: 'Tower 2' },
  { label: 'Townhomes', value: 'Townhome' },
];

function getStatusBadgeVariant(status: string) {
  if (status === 'Active' || status === 'Available') return 'available' as const;
  if (status === 'Pending') return 'pendingSale' as const;
  return 'sold' as const;
}

function ListingCardSkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-6 w-28 mb-2" />
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-3/4 mb-4" />
      <Skeleton className="h-8 w-full rounded-lg" />
    </div>
  );
}

function ListingCard({ listing }: { listing: RealEstateListing }) {
  const isRental = listing.type === 'rental';
  const color = listing.propertyType === 'Townhome' ? '#F0A832' : listing.propertyType === 'Tower 1' ? '#4CC9FF' : '#9B7FFF';

  return (
    <Link href={`/resources/real-estate/${listing.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-[14px] leading-snug mb-1" style={{ color: '#F2F4FF' }}>
              {listing.unitLabel}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[11px] px-2 py-0.5 rounded font-medium"
                style={{ background: `${color}10`, color, border: `1px solid ${color}20` }}
              >
                {listing.propertyType}
              </span>
              {isRental && (
                <span
                  className="text-[11px] px-2 py-0.5 rounded font-medium"
                  style={{ background: 'rgba(45,212,160,0.10)', color: '#2DD4A0', border: '1px solid rgba(45,212,160,0.20)' }}
                >
                  Rental
                </span>
              )}
            </div>
          </div>
          <Badge variant={getStatusBadgeVariant(listing.status)} label={listing.status} />
        </div>

        <div className="font-display font-bold text-[22px] mb-1" style={{ color: '#F2F4FF' }}>
          {listing.priceDisplay}
        </div>

        {listing.summary && (
          <p className="text-[12px] mb-3" style={{ color: '#A7A3C4' }}>{listing.summary}</p>
        )}

        <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: 'rgba(167,163,196,0.70)' }}>
          {listing.description}
        </p>

        <span
          className="flex items-center gap-1 text-[12px] font-medium transition-colors duration-150 group-hover:opacity-80"
          style={{ color }}
        >
          View details <ArrowRight size={11} />
        </span>
      </div>
    </Link>
  );
}

export default function RealEstatePage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const filtered = mockListingsRE.filter((l) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'listing') return l.type === 'listing';
    if (activeFilter === 'rental') return l.type === 'rental';
    return l.propertyType === activeFilter;
  });

  const activeCount = mockListingsRE.filter((l) => l.status === 'Active' || l.status === 'Available').length;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Real Estate"
          title="Panorama Real Estate"
          description="Listings, rentals, market context, and owner resources — all specific to Panorama Towers. Mocked data for demonstration."
          stat={{ value: String(activeCount), label: 'active listings' }}
        />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8 space-y-14">

          {/* Disclaimer */}
          <div
            className="px-5 py-4 rounded-xl"
            style={{ background: 'rgba(240,168,50,0.04)', border: '1px solid rgba(240,168,50,0.15)' }}
          >
            <div className="flex items-start gap-3">
              <Info size={15} style={{ color: '#F0A832', marginTop: 1, flexShrink: 0 }} />
              <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.70)' }}>
                <span className="font-semibold" style={{ color: '#F0A832' }}>Mocked data only. </span>
                All listings, prices, and market data on this page are illustrative examples only. They do not represent actual MLS listings or current market conditions. For accurate real estate information, consult a licensed real estate professional.
              </p>
            </div>
          </div>

          {/* Listings */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <Home size={18} style={{ color: '#4CC9FF' }} />
              <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
                Current Listings & Rentals
              </h2>
            </div>

            <div className="mb-6">
              <FilterRow filters={listingFilters} active={activeFilter} onChange={setActiveFilter} />
            </div>

            {isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 4 }).map((_, i) => <ListingCardSkeleton key={i} />)}
              </div>
            )}

            {!isLoading && filtered.length === 0 && (
              <EmptyState
                title="No listings match this filter"
                description="Try a different filter or check back soon."
                icon={<Home size={22} style={{ color: '#4CC9FF' }} />}
                action={
                  <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveFilter('all')}>
                    Show all listings
                  </button>
                }
              />
            )}

            {!isLoading && filtered.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
            )}
          </div>

          <div className="neon-divider" />

          {/* Market Snapshot */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <TrendingUp size={18} style={{ color: '#2DD4A0' }} />
              <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
                Market Snapshot
              </h2>
            </div>
            <p className="text-[13px] mb-5" style={{ color: '#A7A3C4' }}>
              {marketSnapshotData.disclaimer}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {marketSnapshotData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="neon-card p-4 text-center"
                >
                  <div className="font-display font-bold text-[22px] mb-1" style={{ color: '#F2F4FF' }}>
                    {stat.value}
                  </div>
                  <p className="text-[11px] leading-snug" style={{ color: '#A7A3C4' }}>{stat.label}</p>
                  <p className="text-[10px] mt-1" style={{ color: 'rgba(167,163,196,0.35)' }}>{stat.note}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="neon-divider" />

          {/* Townhomes section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Building2 size={18} style={{ color: '#F0A832' }} />
              <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
                Townhomes at Panorama
              </h2>
            </div>
            <div
              className="neon-card p-6"
            >
              <p className="text-[14px] leading-relaxed mb-4" style={{ color: '#A7A3C4' }}>
                Panorama includes a collection of townhomes in addition to the tower units. Townhomes offer private street-level entry, attached garages, and a more house-like layout — while still providing full access to Panorama's tower amenities including the pool, fitness center, and 24-hour concierge.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Private Entry', desc: 'Street-level access, no elevator required' },
                  { label: 'Attached Garage', desc: 'Two-car garage standard in most townhome units' },
                  { label: 'Full Amenity Access', desc: 'Pool, fitness center, concierge — same as tower residents' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(240,168,50,0.05)', border: '1px solid rgba(240,168,50,0.12)' }}
                  >
                    <p className="font-semibold text-[13px] mb-1" style={{ color: '#F0A832' }}>{item.label}</p>
                    <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="neon-divider" />

          {/* Owner Resources */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <Home size={18} style={{ color: '#9B7FFF' }} />
              <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
                Owner Resources
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ownerResources.map((resource) => (
                <div key={resource.id} className="neon-card p-5">
                  <div className="flex items-start gap-3">
                    <span className="text-[22px] flex-shrink-0">{resource.icon}</span>
                    <div>
                      <h4 className="font-display font-semibold text-[14px] mb-1" style={{ color: '#F2F4FF' }}>
                        {resource.title}
                      </h4>
                      <p className="text-[13px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {resource.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="neon-divider" />

          {/* Specialist Spotlight */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <User size={18} style={{ color: '#4CC9FF' }} />
              <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
                Specialist Spotlight
              </h2>
            </div>
            <div
              className="neon-card p-6"
            >
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[18px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
                    border: '1px solid rgba(76,201,255,0.20)',
                    color: '#4CC9FF',
                  }}
                >
                  {specialistSpotlight.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div>
                  <h3 className="font-display font-bold text-[18px] mb-1" style={{ color: '#F2F4FF' }}>
                    {specialistSpotlight.name}
                  </h3>
                  <p className="text-[13px]" style={{ color: '#A7A3C4' }}>{specialistSpotlight.title}</p>
                </div>
              </div>
              <p className="text-[14px] leading-relaxed mb-4" style={{ color: '#A7A3C4' }}>
                {specialistSpotlight.bio}
              </p>
              <div className="mb-4">
                <p className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(167,163,196,0.45)' }}>
                  Specialties
                </p>
                <div className="flex flex-wrap gap-2">
                  {specialistSpotlight.specialties.map((s) => (
                    <span
                      key={s}
                      className="text-[12px] px-2.5 py-1 rounded"
                      style={{ background: 'rgba(76,201,255,0.06)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.15)' }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <div
                className="px-4 py-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.55)' }}>
                  {specialistSpotlight.note}
                </p>
              </div>
            </div>
          </div>

          {/* Thinking of selling */}
          <div
            className="px-6 py-6 rounded-xl"
            style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
          >
            <h3 className="font-display font-bold text-[18px] mb-2" style={{ color: '#F2F4FF' }}>
              Thinking of Selling or Leasing?
            </h3>
            <p className="text-[14px] leading-relaxed mb-4" style={{ color: '#A7A3C4' }}>
              Panorama has a strong owner community and a resident specialist with deep building knowledge. If you're considering selling or leasing your unit, connecting with a Panorama-familiar agent is worth the conversation.
            </p>
            <button className="btn-secondary text-[13px] px-5 py-2.5 inline-flex items-center gap-2">
              Connect via Directory <ArrowRight size={13} />
            </button>
          </div>

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
